<template>
  <div class="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow">
   
      <section class="space-y-6">
          <!-- Form -->
          <form @submit.prevent="calculate" class="space-y-4">
              <div
                  class="flex items-center justify-between border-l-4 border-emerald-400 pl-2"
              >
                  <label class="text-sm text-gray-600"
                      >Loan Amount<span class="text-red-500">*</span></label
                  >
                  <div class="relative w-2/3">
                      <span
                          class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                          >$</span
                      >
                      <input
                          type="text"
                          v-model="principalDisplay"
                          @blur="formatPrincipal"
                          class="w-full pl-6 bg-gray-100 text-right p-2 rounded focus:outline-none"
                          placeholder="0"
                      />
                  </div>
              </div>
              <div
                  class="flex items-center justify-between border-l-4 border-emerald-400 pl-2"
              >
                  <label class="text-sm text-gray-600"
                      >Interest Rate<span class="text-red-500">*</span></label
                  >
                  <div class="relative w-2/3">
                      <input
                          type="number"
                          v-model.number="annualInterestRate"
                          step="0.01"
                          class="w-full pr-8 bg-gray-100 text-right p-2 rounded focus:outline-none"
                          placeholder="0"
                      />
                      <span
                          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                          >%</span
                      >
                  </div>
              </div>
              <div
                  class="flex items-center justify-between border-l-4 border-emerald-400 pl-2"
              >
                  <label class="text-sm text-gray-600"
                      >Loan Term<span class="text-red-500">*</span></label
                  >
                  <div class="relative w-2/3">
                      <input
                          type="number"
                          v-model.number="years"
                          class="w-full pr-16 bg-gray-100 text-right p-2 rounded focus:outline-none"
                          placeholder="0"
                      />
                      <span
                          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                          >years</span
                      >
                  </div>
              </div>
              <div class="flex items-center justify-between">
                  <label class="text-sm text-gray-400 italic"
                      >Offset Account (optional)</label
                  >
                  <div class="relative w-2/3">
                      <span
                          class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                          >$</span
                      >
                      <input
                          type="text"
                          v-model="offsetDisplay"
                          @blur="formatOffset"
                          class="w-full pl-6 bg-gray-100 text-right p-2 rounded focus:outline-none"
                          placeholder="0"
                      />
                  </div>
              </div>
              <div class="flex items-center justify-between">
                  <label class="text-sm text-gray-400 italic"
                      >Loan Fee (optional)</label
                  >
                  <div class="flex w-2/3 items-center space-x-2">
                      <div class="relative flex-1">
                          <span
                              class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                              >$</span
                          >
                          <input
                              type="number"
                              v-model.number="fee"
                              class="w-full pl-6 bg-gray-100 text-right p-2 rounded focus:outline-none"
                              placeholder="0"
                          />
                      </div>
                      <select
                          v-model="feeFrequency"
                          class="bg-gray-100 p-2 rounded focus:outline-none"
                      >
                          <option value="annually">Annually</option>
                          <option value="monthly">Monthly</option>
                          <option value="fortnightly">Fortnightly</option>
                          <option value="weekly">Weekly</option>
                      </select>
                  </div>
              </div>
              <div
                  class="flex items-center justify-between border-l-4 border-indigo-400 pl-2"
              >
                  <label class="text-sm text-gray-600"
                      >Repayment Frequency<span class="text-red-500"
                          >*</span
                      ></label
                  >
                  <select
                      v-model.number="paymentsPerYear"
                      class="w-2/3 bg-gray-100 p-2 rounded focus:outline-none"
                  >
                      <option :value="12">Monthly</option>
                      <option :value="26">Fortnightly</option>
                      <option :value="52">Weekly</option>
                  </select>
              </div>
              <div class="flex space-x-4 mt-4">
                  <button
                      type="button"
                      @click="resetForm"
                      class="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300 transition cursor-pointer"
                  >
                      Reset
                  </button>
                  <button
                      type="submit"
                      :disabled="!canCalculate"
                      class="flex-1 bg-teal-500 text-white py-2 rounded hover:bg-teal-600 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                  >
                      Calculate
                  </button>
              </div>
          </form>

          <p class="text-xs text-center">
              Disclaimer: This calculator provides illustrative estimates
              based on your inputs and underlying assumptions. Results are for
              guidance only—please consult us for advice tailored to your
              situation. Financial Calculators © GetMyLending Pty Ltd 2025
              Estimate your regular loan repayments by entering your loan
              amount and choosing a repayment frequency—monthly, fortnightly
              or weekly. This calculator shows you both Principal & Interest
              repayment amounts over your chosen loan term.
          </p>
         

          <section v-if="showResults" class="space-y-3" ref="resultsSection">
              <h2 class="text-3xl text-center font-semibold text-gray-700">
                  Your results
              </h2>
              <div class="bg-gray-50 p-4 rounded space-y-2">
                  <div class="flex justify-between">
                      <span class="text-sm text-gray-600"
                          >Periodic Repayment</span
                      >
                      <span class="font-medium text-gray-800">{{
                          formatCurrency(payment)
                      }}</span>
                  </div>
                  <div class="flex justify-between">
                      <span class="text-sm text-gray-600"
                          >Total Interest + Fees</span
                      >
                      <span class="font-medium text-gray-800">{{
                          formatCurrency(totalInterest)
                      }}</span>
                  </div>
                  <div class="flex justify-between">
                      <span class="text-sm text-gray-600">Total Payment</span>
                      <span class="font-medium text-gray-800">{{
                          formatCurrency(totalPayment)
                      }}</span>
                  </div>
              </div>
          </section>

          <section v-show="showChart" class="mt-6" ref="chartSection">
              <h2 class="text-4xl text-center font-semibold text-gray-700 mb-3">
                  Loan Balance Chart
              </h2>
              <div class="bg-gray-50 p-4 rounded shadow-inner">
                  <canvas ref="chartRef" class="w-full h-[400px]"></canvas>
              </div>
          </section>

          <span class="block text-center font-normal mb-2 font-heading">Assumptions:</span>
          <ul class="text-xs text-center">
              <li>
                  <strong>Fees:</strong> Only ongoing fees apply. Any loan fee
                  is spread evenly across each repayment.
              </li>
              <li>
                  <strong>Offset:</strong> Reduces interest each period
                  (balance – offset) but does not change your fixed P&I
                  payment.
              </li>
              <li>
                  <strong>Repayments:</strong> A single P&I amount is
                  calculated up front and stays the same.
              </li>
              <li>
                  <strong>Frequency:</strong> Payments and interest
                  compounding match your choice: <code>12</code> (monthly),
                  <code>26</code> (fortnightly), or <code>52</code> (weekly).
              </li>
              <li>
                  <strong>Schedule:</strong> Amortisation runs until you've
                  paid off the balance or hit the total number of payments.
              </li>
              <li>
                  <strong>Term:</strong> Entered in whole years; each year
                  counts as exactly 52 weeks.
              </li>
          </ul>
      </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import Chart from "chart.js/auto";

const principal = ref(0);
const principalDisplay = ref("");
const annualInterestRate = ref(0);
const years = ref(0);
const paymentsPerYear = ref(12);
const offset = ref(0);
const offsetDisplay = ref("");
const fee = ref(0);
const feeFrequency = ref("annually");
const payment = ref(0);
const amortizationSchedule = ref([]);
const calculated = ref(false);

// Refs for scrolling
const resultsSection = ref(null);
const chartSection = ref(null);

const chartRef = ref(null);
let balanceChart = null;

const freqMap = { annually: 1, monthly: 12, fortnightly: 26, weekly: 52 };

// Convert fee into a periodic amount based on its frequency
const periodicFee = computed(() => {
  const annualFee = fee.value * freqMap[feeFrequency.value];
  return annualFee / paymentsPerYear.value;
});
const totalFees = computed(
  () => periodicFee.value * amortizationSchedule.value.length
);
const totalInterestOnly = computed(() =>
  amortizationSchedule.value.reduce(
      (sum, e) => sum + (e.interestPayment || 0),
      0
  )
);
const totalInterest = computed(() => totalInterestOnly.value + totalFees.value);
// Total payment = principal + total interest (incl. fees)
const totalPayment = computed(() => principal.value + totalInterest.value);

// Base scheduled principal+interest repayment, unaffected by offset
const basePaymentComputed = computed(() => {
  const P = principal.value;
  const i = annualInterestRate.value / 100 / paymentsPerYear.value;
  const n = years.value * paymentsPerYear.value;
  return i === 0 ? P / n : (i * P) / (1 - Math.pow(1 + i, -n));
});

const showResults = computed(() => calculated.value && payment.value > 0);
const showChart = computed(
  () => calculated.value && amortizationSchedule.value.length > 0
);
const canCalculate = computed(
  () => principal.value > 0 && annualInterestRate.value > 0 && years.value > 0
);

// Dynamic label for repayment based on frequency
const repaymentLabel = computed(() => {
  switch (paymentsPerYear.value) {
      case 12:
          return "Monthly Repayment";
      case 26:
          return "Fortnightly Repayment";
      case 52:
          return "Weekly Repayment";
      default:
          return "Periodic Repayment";
  }
});

watch(principal, (val) => {
  principalDisplay.value = val ? val.toLocaleString() : "";
  calculated.value = false;
});
watch(principalDisplay, (val) => {
  principal.value = Number(val.replace(/,/g, "")) || 0;
  calculated.value = false;
});
watch(offset, (val) => {
  offsetDisplay.value = val ? val.toLocaleString() : "";
  calculated.value = false;
});
watch(offsetDisplay, (val) => {
  offset.value = Number(val.replace(/,/g, "")) || 0;
  calculated.value = false;
});
[annualInterestRate, years, paymentsPerYear, fee, feeFrequency].forEach(
  (refVar) =>
      watch(refVar, () => {
          calculated.value = false;
      })
);

function formatPrincipal() {
  principalDisplay.value = principal.value.toLocaleString();
}
function formatOffset() {
  offsetDisplay.value = offset.value.toLocaleString();
}

// Function to scroll to results
function scrollToResults() {
  nextTick(() => {
      if (resultsSection.value) {
          resultsSection.value.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
          });
      }
  });
}

async function calculate() {
  // sync inputs
  principal.value = Number(principalDisplay.value.replace(/,/g, "")) || 0;
  offset.value = Number(offsetDisplay.value.replace(/,/g, "")) || 0;

  const P = principal.value;
  const r = annualInterestRate.value / 100 / paymentsPerYear.value;
  const n = years.value * paymentsPerYear.value;
  if (n <= 0) return;

  const base = basePaymentComputed.value;
  payment.value = base + periodicFee.value;

  // build amortization until paid off or max periods
  amortizationSchedule.value = [];
  let balance = P;
  let count = 0;
  while (balance > 0 && count < n) {
      const ib = Math.max(balance - offset.value, 0);
      const ip = ib * r;
      const pp = base - ip;
      balance = Math.max(balance - pp, 0);
      amortizationSchedule.value.push({
          balance,
          interestPayment: ip,
          principalPayment: pp,
      });
      count++;
  }
  calculated.value = true;
  // wait for chart DOM then render
  await nextTick();
  renderChart();
  
  // Scroll to results section
  scrollToResults();
}

function resetForm() {
  principalDisplay.value = "";
  principal.value = 0;
  annualInterestRate.value = 0;
  years.value = 0;
  paymentsPerYear.value = 12;
  offsetDisplay.value = "";
  offset.value = 0;
  fee.value = 0;
  feeFrequency.value = "annually";
  payment.value = 0;
  amortizationSchedule.value = [];
  calculated.value = false;
  if (balanceChart) balanceChart.destroy();
  if (balanceChart) {
      balanceChart.destroy();
      balanceChart = null;       // ← clear the reference so renderChart creates anew
  }
}

async function renderChart() {
  const labels = Array.from({ length: years.value + 1 }, (_, i) => i);
  const balanceData = labels.map((yr, idx) =>
      idx === 0
          ? principal.value
          : amortizationSchedule.value[yr * paymentsPerYear.value - 1]
                ?.balance || 0
  );
  const tp = payment.value * years.value * paymentsPerYear.value;
  const remData = labels.map(
      (yr) => tp - payment.value * paymentsPerYear.value * yr
  );
  const ctx = chartRef.value?.getContext("2d");
  if (!ctx) return;
  if (balanceChart) {
      balanceChart.data.labels = labels;
      balanceChart.data.datasets[0].data = balanceData;
      balanceChart.data.datasets[1].data = remData;
      balanceChart.update();
  } else {
      balanceChart = new Chart(ctx, {
          type: "line",
          data: {
              labels,
              datasets: [
                  {
                      label: "Loan Balance",
                      data: balanceData,
                      borderColor: "#10B981",
                      backgroundColor: "#10B981",
                      fill: true,
                      tension: 0.4,
                  },
                  {
                      label: "Total Payment",
                      data: remData,
                      borderColor: "#1F2937",
                      backgroundColor: "#1F2937",
                      fill: true,
                      tension: 0,
                  },
              ],
          },
          options: {
              scales: {
                  x: {
                      title: { display: true, text: "Years" },
                      ticks: { stepSize: 1 },
                  },
                  y: { title: { display: true, text: "Amount" } },
              },
              plugins: {
                  tooltip: {
                      callbacks: {
                          label: (ctx) => formatCurrency(ctx.parsed.y),
                      },
                  },
                  legend: { display: true, position: "top" },
              },
              interaction: { mode: "index", intersect: false },
          },
      });
  }
}

watch(
  amortizationSchedule,
  async (newVal) => {
      if (newVal.length && calculated.value) {
          await nextTick();
          renderChart();
      }
  },
  { deep: true }
);

function formatCurrency(value) {
  return value.toLocaleString(undefined, {
      style: "currency",
      currency: "AUD",
  });
}
</script>

<style scoped>
/* Tailwind handles styling */
</style>